/**
 * React context for the WardkeyClient.
 *
 * The console always talks to the API through its same-origin BFF at
 * `/bff/api/*` — that's the only way the browser can reach api.wardkey.app
 * without exposing bearer tokens to the DOM. The cookie attached to the
 * request is the session cookie minted at OAuth-callback time; the BFF
 * resolves it to a short-lived identity token before forwarding.
 *
 * Construct one client per app load, wrap the authenticated route tree
 * with <ApiClientProvider />, and call useApiClient() from any descendant.
 */

import { WardkeyClient } from "@wardkey/sdk";
import { type ReactNode, createContext, useContext, useMemo } from "react";

const ApiClientContext = createContext<WardkeyClient | null>(null);

export type ApiClientProviderProps = {
  /** Override the default BFF base URL. Tests use this to inject a mock. */
  baseUrl?: string;
  /** Custom fetch implementation — useful for tests. */
  fetch?: typeof fetch;
  children: ReactNode;
};

const DEFAULT_BASE_URL = "/bff/api";

/**
 * Lazily construct a single WardkeyClient per provider instance. Tests can
 * mount a fresh provider per case; production renders one at the root of
 * the authenticated tree.
 */
export function ApiClientProvider({
  baseUrl = DEFAULT_BASE_URL,
  fetch: customFetch,
  children,
}: ApiClientProviderProps) {
  const client = useMemo(
    () =>
      new WardkeyClient({
        baseUrl,
        useCookies: true,
        ...(customFetch ? { fetch: customFetch } : {}),
      }),
    [baseUrl, customFetch],
  );
  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>;
}

/**
 * Access the singleton WardkeyClient for the current authenticated tree.
 *
 * Throws if called outside an <ApiClientProvider /> — that's almost
 * certainly a bug (e.g. using the client from inside the login route),
 * and silent fallback would mask it.
 */
export function useApiClient(): WardkeyClient {
  const client = useContext(ApiClientContext);
  if (!client) {
    throw new Error("useApiClient must be called inside <ApiClientProvider />");
  }
  return client;
}
